// Lightweight canvas signature pad. Captures pointer strokes, draws them, and
// exports a PNG blob via `toBlob()`. No external deps. Designed to be mounted
// inside an existing element and torn down with `destroy()`.

export class SignaturePad {
  constructor({ width = 480, height = 160 } = {}) {
    this.width = width;
    this.height = height;
    this.el = document.createElement('div');
    this.el.className = 'signature-pad';
    this.el.innerHTML = `
      <canvas width="${width}" height="${height}" style="touch-action:none"></canvas>
    `;
    this.canvas = this.el.querySelector('canvas');
    this.ctx = this.canvas.getContext('2d');
    this._reset();
    this._drawing = false;
    this._dirty = false;
    this._last = null;
    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this.canvas.addEventListener('pointerdown', this._onDown);
    this.canvas.addEventListener('pointermove', this._onMove);
    this.canvas.addEventListener('pointerup', this._onUp);
    this.canvas.addEventListener('pointercancel', this._onUp);
    this.canvas.addEventListener('pointerleave', this._onUp);
  }

  mount(target) {
    target.replaceChildren(this.el);
  }

  isDirty() { return this._dirty; }

  clear() {
    this._reset();
    this._dirty = false;
  }

  // Resolves with a PNG blob, or null when the user has not drawn anything.
  toBlob() {
    if (!this._dirty) return Promise.resolve(null);
    return new Promise((resolve) => this.canvas.toBlob((b) => resolve(b), 'image/png'));
  }

  destroy() {
    this.canvas.removeEventListener('pointerdown', this._onDown);
    this.canvas.removeEventListener('pointermove', this._onMove);
    this.canvas.removeEventListener('pointerup', this._onUp);
    this.canvas.removeEventListener('pointercancel', this._onUp);
    this.canvas.removeEventListener('pointerleave', this._onUp);
    if (this.el.parentNode) this.el.parentNode.removeChild(this.el);
  }

  _reset() {
    this.ctx.fillStyle = '#ffffff';
    this.ctx.fillRect(0, 0, this.width, this.height);
    this.ctx.strokeStyle = '#111';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
  }

  _point(ev) {
    const rect = this.canvas.getBoundingClientRect();
    const x = (ev.clientX - rect.left) * (this.canvas.width / rect.width);
    const y = (ev.clientY - rect.top) * (this.canvas.height / rect.height);
    return { x, y };
  }

  _onDown(ev) {
    ev.preventDefault();
    this.canvas.setPointerCapture?.(ev.pointerId);
    this._drawing = true;
    this._last = this._point(ev);
  }

  _onMove(ev) {
    if (!this._drawing) return;
    const p = this._point(ev);
    this.ctx.beginPath();
    this.ctx.moveTo(this._last.x, this._last.y);
    this.ctx.lineTo(p.x, p.y);
    this.ctx.stroke();
    this._last = p;
    this._dirty = true;
  }

  _onUp(ev) {
    if (!this._drawing) return;
    this._drawing = false;
    try { this.canvas.releasePointerCapture?.(ev.pointerId); } catch (_) {}
  }
}
